import React, { useMemo } from "react";

import {
  DesignElement,
  RequirementLevel1Policy,
  RequirementLevel2Policy,
  RequirementLevel3Policy,
} from "@design-controls/types";
import { QAlert, QStack } from "@qualio/ui-components";

import { useCurrentConfigs } from "../../hooks/useCurrentConfigs";
import { PolicyIssueEnum } from "@design-controls/types";

type GapAnalysisProps = Pick<DesignElement, "policyIssues" | "type">;

const requirementLevels: Array<DesignElement["type"]> = [
  "req1",
  "req2",
  "req3",
];

type Requirements =
  | RequirementLevel1Policy
  | RequirementLevel2Policy
  | RequirementLevel3Policy;

export const GapAnalysis: React.FC<GapAnalysisProps> = ({
  policyIssues: policyIssueRecords,
  type,
}) => {
  const configs = useCurrentConfigs();
  const policiesDefaultOrder = Object.values(PolicyIssueEnum);
  const policyIssues = policiesDefaultOrder.filter(
    (policy) => policyIssueRecords[policy],
  );

  const parentRequirement: Requirements | undefined = useMemo(() => {
    if (!type.startsWith("req") || requirementLevels[0] === type) {
      return;
    }

    const parentType =
      requirementLevels[
        requirementLevels.findIndex((level) => level === type) - 1
      ];
    const config = configs.find((config) => config.type === parentType);

    return config as Requirements;
  }, [configs, type]);

  const childRequirement: Requirements | undefined = useMemo(() => {
    if (
      !type.startsWith("req") ||
      requirementLevels[requirementLevels.length - 1] === type
    ) {
      return;
    }

    const childType =
      requirementLevels[
        requirementLevels.findIndex((level) => level === type) + 1
      ];
    const config = configs.find((config) => config.type === childType);

    return config as Requirements;
  }, [configs, type]);

  const policyMessages = useMemo(() => {
    return policyIssues.map((policyIssue) => {
      let title;
      let description;
      switch (policyIssue) {
        case PolicyIssueEnum.FAILING_TEST:
          title = "Failing tests";
          description = "Test case has failing test result.";
          break;
        case PolicyIssueEnum.OUTDATED_TESTRESULT:
          title = "Outdated test result";
          description = "Test case needs to be re-run.";
          break;
        case PolicyIssueEnum.REQUIRES_CHILD:
          title = "Missing related requirement";
          description = `You must link at least one ${childRequirement?.label} requirement.`;
          break;
        case PolicyIssueEnum.REQUIRES_PARENT:
          title = "Missing related requirement";
          description = type.startsWith("testCase")
            ? "Tests must be linked to at least one requirement."
            : `You must link at least one ${parentRequirement?.label} requirement.`;
          break;
        case PolicyIssueEnum.UNMITIGATED:
          title = "Unmitigated risks";
          description = "Risk needs mitigation based on policy.";
          break;
        case PolicyIssueEnum.UNTESTED_REQUIREMENT:
          title = "Missing related test case";
          description = "Requirement must be linked to at least one test case.";
          break;
      }

      return {
        title,
        description,
        policyIssue,
      };
    });
  }, [policyIssues, childRequirement, parentRequirement]);

  if (!policyIssues.length) {
    return null;
  }

  return (
    <QStack gap="16px">
      <QAlert
        description={
          policyMessages.length === 1 ? (
            policyMessages[0].description
          ) : (
            <ul>
              {policyMessages.map((policyMessage) => (
                <li data-cy={policyMessage.policyIssue}>
                  {policyMessage.description}
                </li>
              ))}
            </ul>
          )
        }
        title={
          policyMessages.length === 1 ? policyMessages[0].title : "Open issues"
        }
        status="warning"
        data-cy={
          policyMessages.length === 1
            ? policyMessages[0].policyIssue
            : undefined
        }
      />
    </QStack>
  );
};

// ensure QHeader can show component
GapAnalysis.displayName = "QAlert";
